import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ImageUploadField } from "@/components/ui/image-upload-field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Coins, Gem, RefreshCw } from "lucide-react";
import { useState, useEffect } from "react";

interface ShopItemDialogProps {
  readonly isOpen: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSubmit: (data: any) => void;
  readonly initialData?: any;
  readonly mode: "add" | "edit";
  readonly isLoading?: boolean;
}

export function ShopItemDialog({
  isOpen,
  onOpenChange,
  onSubmit,
  initialData,
  mode,
  isLoading,
}: ShopItemDialogProps) {
  const [formData, setFormData] = useState(
    initialData ?? {
      name: "",
      description: "",
      imageUrl: "",
      price: 100,
      currencyType: "regular",
      itemType: "avatar",
      category: "decoration",
      isActive: true,
      managerMode: false,
      stock: null as number | null,
    }
  );

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{mode === "add" ? "Add New Shop Item" : "Edit Shop Item"}</DialogTitle>
          <DialogDescription>
            {mode === "add" ? "Enter the details for the new shop item." : "Update the details for this shop item."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Item Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Enter item name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="itemType">Item Type</Label>
                <Select
                  value={formData.itemType}
                  onValueChange={(value: any) =>
                    setFormData({ ...formData, itemType: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select item type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="avatar">Avatar</SelectItem>
                    <SelectItem value="banner">Banner</SelectItem>
                    <SelectItem value="board">Board Background</SelectItem>
                    <SelectItem value="cardDesign">Card Design</SelectItem>
                    <SelectItem value="booster">Booster Pack</SelectItem>
                    <SelectItem value="theme">Game Theme</SelectItem>
                    <SelectItem value="special">Special Item</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Enter item description"
                required
              />
            </div>

            <div className="space-y-2">
              <ImageUploadField
                id="imageUrl"
                label="Image URL (Optional)"
                value={formData.imageUrl ?? ""}
                onChange={(value) =>
                  setFormData({ ...formData, imageUrl: value })
                }
                placeholder="Enter image URL or upload an image"
                uploadEndpoint="/api/manager/upload-image"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price">Price</Label>
                <Input
                  id="price"
                  type="number"
                  min="1"
                  step="1"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      price: parseInt(e.target.value) || 0,
                    })
                  }
                  placeholder="100"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="currencyType">Currency Type</Label>
                <Select
                  value={formData.currencyType}
                  onValueChange={(value: "regular" | "premium") =>
                    setFormData({ ...formData, currencyType: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select currency type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="regular">
                      <div className="flex items-center">
                        <Coins className="w-4 h-4 mr-2 text-yellow-500" />
                        Regular (Coins)
                      </div>
                    </SelectItem>
                    <SelectItem value="premium">
                      <div className="flex items-center">
                        <Gem className="w-4 h-4 mr-2 text-purple-500" />
                        Premium (Gems)
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value: string) =>
                    setFormData({ ...formData, category: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="decoration">Decoration</SelectItem>
                    <SelectItem value="appearance">Appearance</SelectItem>
                    <SelectItem value="enhancement">Enhancement</SelectItem>
                    <SelectItem value="special">Special</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="stock">Stock (Empty for unlimited)</Label>
                <Input
                  id="stock"
                  type="number"
                  min="0"
                  step="1"
                  value={formData.stock === null ? "" : formData.stock}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      stock: e.target.value === "" ? null : parseInt(e.target.value),
                    })
                  }
                  placeholder="Leave empty for unlimited"
                />
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isActive: checked })
                  }
                />
                <Label htmlFor="isActive">Active</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="managerMode"
                  checked={formData.managerMode}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, managerMode: checked })
                  }
                />
                <Label htmlFor="managerMode">Manager Mode</Label>
                <span className="text-xs text-gray-400">(Only visible to managers)</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading} className="ml-2">
              {isLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  {mode === "add" ? "Adding..." : "Updating..."}
                </>
              ) : (
                <>{mode === "add" ? "Add Item" : "Update Item"}</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}